// DePaul University: public college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - DePaul's Common Data Set 2025-2026, posted section by section (one PDF
//    per section, 2025CDS_A.pdf to 2025CDS_J.pdf) on the Office of
//    Institutional Research and Market Analytics' Common Data Set page
//    (irma.depaul.edu/FFPlus.asp?cont=cds), the newest edition it posts.
//    Section C: C1 applicants, admits and enrollees for Fall 2025 (the C1
//    residency table is left blank, with totals of 0, so no residency rows
//    here), C8 test policy for Fall 2027, C21/C22. Section G: G1/G5 costs,
//    which the file gives for 2026-2027. Departures: the C21 early decision
//    checkbox is ambiguous in the PDF's text, so the early decision answer
//    rests on the admissions pages (they list no early decision plan; the
//    Early Action Program FAQ calls early action non-binding); and
//    the CDS G1 tuition is $48,179 while DePaul's 2026-27 Undergraduate
//    Tuition and Fees page shows $48,180 ($16,060 a term, three terms) for
//    students admitted in 2026. The page uses the CDS figure, so the rows
//    sum to the CDS budget.
//  - DePaul Undergraduate Admission (the freshman application types,
//    deadlines, notification dates, School of Music and The Theatre School
//    rules), its Deadlines page, its Early Action Program FAQ, its
//    Test-Optional FAQs, and its page on the extra Animation and Film and
//    Television requirements. The pages carry no cycle year; the Apply page
//    says incoming freshmen for Fall/Winter 2027 apply on the Common App.
//  - DePaul Tuition and Aid, Cost of Attendance and Undergraduate Tuition
//    and Fees (2026-2027), read only to check the CDS: the Cost of
//    Attendance page gives the same books ($1,104), food and housing
//    ($20,766), transportation ($1,290) and personal ($4,794) figures; the
//    tuition page lists $50,403 for Music and Theatre students admitted in
//    2026. Neither is cited on the page.
//  - College Scorecard, school id 144740 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'DePaul Common Data Set 2025-2026',
        url: 'https://irma.depaul.edu/FFPlus.asp?cont=cds',
        read: '2026-10-01',
    },
    admission: {
        label: 'DePaul Undergraduate Admission',
        url: 'https://www.depaul.edu/admission/undergraduate-admission',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'DePaul Undergraduate Admission, Deadlines',
        url: 'https://www.depaul.edu/admission/undergraduate-admission/deadlines',
        read: '2026-10-01',
    },
    earlyAction: {
        label: 'DePaul Undergraduate Admission, Early Action Program FAQ',
        url: 'https://www.depaul.edu/admission/undergraduate-admission/early-action-program-faq',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'DePaul Undergraduate Admission, Test-Optional FAQs',
        url: 'https://www.depaul.edu/admission/undergraduate-admission/test-optional-faqs',
        read: '2026-10-01',
    },
    animationFilm: {
        label: 'DePaul Undergraduate Admission, Animation and Film and Television requirements',
        url: 'https://www.depaul.edu/admission/undergraduate-admission/cdm-animation',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?144740-DePaul-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'depaul-university',
    kbId: 'depaul_university',
    name: 'DePaul University',
    shortName: 'DePaul',
    location: 'Chicago, IL',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (DePaul brand guidelines, colors);
    // decoration only.
    branding: {
        primary: '#003DA5',
        secondary: '#CE112D',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/depaul_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 27462,
        admitted: 24111,
        enrolled: 2798,
        source: 'cds',
        where: 'section C1 (men and women added together)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'DePaul\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'DePaul\'s Early Action Program is non-binding: complete your application by November 15 and you receive a decision by December 15, and you are not required to commit to DePaul. DePaul publishes no early action applicant or admit counts, so there is no early acceptance rate, and its admissions pages list no early decision plan.',
        source: 'earlyAction',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most applicants apply to DePaul on one deadline. A few programs ask for more:',
        rows: [
            { school: 'School of Music', requirement: 'Apply by December 1, with auditions, interviews and application fees. Notification by April 1. Not eligible for the Early Action Program.', source: 'admission' },
            { school: 'The Theatre School', requirement: 'Acting and Stage Management apply by December 1; all other Theatre majors by February 1. Notification by April 1. Not eligible for the Early Action Program.', source: 'admission' },
            { school: 'Animation (BA, BFA)', requirement: 'A Creative Statement and a Creative Portfolio are required.', source: 'animationFilm' },
            { school: 'Film and Television (BA, BFA)', requirement: 'A Creative Statement is required; a Creative Portfolio is strongly encouraged but not required.', source: 'animationFilm' },
        ],
        source: 'admission',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Notification', value: 'February 1' },
        rows: [
            { plan: 'Early Action Program', deadline: 'November 15', decision: 'By December 15', binding: false },
            { plan: 'Regular Notification', deadline: 'February 1', decision: 'By March 15', binding: false },
        ],
        note: 'Classes begin in September. Applying by November 15 (Early Action Program) or by February 1 gives the strongest scholarship consideration, and early applicants also receive priority advising and course registration.',
        source: 'admission',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'DePaul is test-optional: you choose whether to submit ACT or SAT scores, and you say on the Common App, in the DePaul Questions, whether you are applying test optional. If you apply test optional, scores sent by a testing agency or shown on your transcript are not taken into consideration. Test-optional applicants stay eligible for scholarships and the University Honors Program.',
        source: 'testOptional',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 48179 },
            { item: 'Required fees', amount: 900 },
            { item: 'Housing (on campus)', amount: 12477 },
            { item: 'Food (on-campus meal plan)', amount: 8289 },
            { item: 'Books and supplies', amount: 1104 },
            { item: 'Transportation', amount: 1290 },
            { item: 'Other expenses', amount: 4794 },
        ],
        total: 77033,
        note: 'For a full-time first-year student living on campus, the full academic year, as the Common Data Set gives it. DePaul says tuition and fees vary by year of study and by program, with 2% of full-time undergraduates paying more than this tuition.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 27108,
            byIncome: [
                { income: '$0 to $30,000', amount: 27093 },
                { income: '$30,001 to $48,000', amount: 21638 },
                { income: '$48,001 to $75,000', amount: 24343 },
                { income: '$75,001 to $110,000', amount: 26128 },
                { income: 'Over $110,000', amount: 36091 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from DePaul\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
